import { Component, computed, effect, signal, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AssetDetail } from '@lp-ui-lib';
import { HistoryItemDetail } from '@lp-ui-lib';
import { Sensor } from '@lp-ui-lib';
import { DateFormatSettings } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { SettingsService } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { SpinnerComponent } from '@lp-ui-lib';
import { SidebarComponent } from '@lp-ui-lib';
import { BreadcrumbsComponent } from '@lp-ui-lib';
import { NavButtonComponent } from '@lp-ui-lib';
import { HistoryItemComponent } from '../../../../components/history-item/history-item';
import { HeaderComponent } from '@lp-ui-lib';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetsService } from '@lp-ui-lib';
import { DocumentationWizardComponent } from '../../../../components/documentation-wizard/documentation-wizard';

@Component({
  selector: 'app-asset-sensor-history',
  imports: [
    HeaderComponent,
    SpinnerComponent,
    SidebarComponent,
    BreadcrumbsComponent,
    NavButtonComponent,
    HistoryItemComponent,
    NzPaginationModule,
    DocumentationWizardComponent,
  ],
  templateUrl: './asset-sensor-history.html',
  styleUrls: ['./asset-sensor-history.scss'],
})
export class AssetSensorHistory {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  public settings = inject(SettingsService);
  public assetsService = inject(AssetsService);

  public asset = signal<AssetDetail | any>(null);
  public sensor = signal<Sensor | null>(null);
  public loading = signal(false);
  public overlayDocumentation = signal(false);
  public thumbnailLoading = signal<boolean | null>(null);
  public thumbnailError = signal(false);
  public breadcrumbsLoading = signal(false);
  public historyItems = signal<HistoryItemDetail[]>([]);
  public documentationAdded = signal(false);
  public assetID = signal<number>(0);
  public sensorID = signal<number>(0);
  public measurementID = signal<number | null>(null);
  public dateFormat = signal<DateFormatSettings | null>(null);

  public pageSize = signal(10);
  public pageIndex = signal(1);

  public readonly HistoryItems = computed(() =>
    this.historyItems().slice(
      this.pageSize() * (this.pageIndex() - 1),
      this.pageSize() * this.pageIndex()
    )
  );

  public readonly isMobile = computed(() => this.ui.IsMobile);
  public readonly isTablet = computed(() => this.ui.IsTablet);
  public readonly isDesktop = computed(() => this.ui.IsDesktop);

  public readonly isRotating = computed(() => this.asset()?.equipmentType === 'Rotating Equipment');

  private readonly routeEffect = effect(() => {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: any) => {
      this.assetID.set(+params.get('id'));
      this.sensorID.set(+params.get('sensorid'));
      this.measurementID.set(+params.get('measurementid'));
      this.refresh();
    });

    this.ui.onNavBack(() => {
      this.router.navigate(['asset', this.assetID(), this.sensorID()], {
        replaceUrl: true,
      });
    });

    this.ui.updateTabIndex(this.route.snapshot.data['tabIndex']);
    this.ui.updateBackBtn(this.route.snapshot.data['backBtn']);
  });

  public refresh(disableCache = false) {
    this.loading.set(true);
    this.documentationAdded.set(false);

    this.settings.getUserSettings().then(async (settings: any) => {
      this.dateFormat.set(settings.DateFormat);
      const result: any = await this.assetsService.getAssetEM(
        this.assetID()!,
        disableCache,
        settings.MeasurementSystem
      );
      this.loading.set(false);
      this.breadcrumbsLoading.set(result.breadcrumbsLoading);
      this.asset.set(result?.asset);

      const sensor = result?.asset?.sensors.find(
        (_sensor: any) => _sensor.sensorID === this.sensorID()
      );
      this.sensor.set(sensor || null);

      this.ui.setBackText(
        this.i18n.parse(this.asset()?.objectName) +
          (sensor ? ' | ' + this.i18n.parse(sensor.sensorDisplayLabel) : '')
      );

      if (!!result?.asset?.picture && this.thumbnailLoading() === null) {
        this.thumbnailLoading.set(true);
        this.thumbnailError.set(false);
      }
      this.loading.set(false);

      this.assetsService
        .getObjectHistory(this.assetID()!, this.sensorID()!)
        .then((result: HistoryItemComponent[]) => {
          const filtered: any = result.filter(
            (item: any) =>
              !this.sensorID() ||
              this.sensorID()! < 0 ||
              item.sensorID === this.sensorID() ||
              !item.sensorID ||
              item.sensorID < 0
          );
          this.historyItems.set(filtered);
        });
    });
  }

  public onDocumentationAdded() {
    this.refresh();
    this.documentationAdded.set(true);
  }
}
